<!-- TelcoTrack - Root Application Template -->
<!-- Main application shell with conditional rendering based on auth state -->

<div class="app-container" [class.dark]="currentTheme === 'dark'">

  <div *ngIf="routeNavigationLoading" class="tt-route-loading-bar" aria-hidden="true"></div>
  
  <!-- Overlay only — never *ngIf-away the router-outlet. Deep links (/materials/, /maps, …)
       finish initialNavigation before the first CD; a missing outlet leaves a blank canvas. -->
  <div *ngIf="isLoading" class="loading-screen">
    <div class="loading-content">
      <div class="logo">
        <img src="assets/images/ProTelco.png" alt="TelcoTrack - Status Management" />
      </div>
      <app-spinner size="xl"></app-spinner>
      <p class="loading-text">Načítání aplikace...</p>
    </div>
  </div>

  <div *ngIf="routeLoadError" class="app-route-error" role="alert">
    <p>{{ routeLoadError }}</p>
    <a [routerLink]="shellLandingPath">Zpět na přehled</a>
  </div>

  <!-- Main Application (outlet stays mounted while the overlay is up) -->
  <div class="app-main">
    
    <!-- Unauthenticated View (login, privacy when not logged in) -->
    <div *ngIf="!isAuthenticated" class="auth-container">
      <router-outlet></router-outlet>
    </div>

    <!-- Privacy / Legal (standalone, no topbar/sidebar even when authenticated) -->
    <div *ngIf="isAuthenticated && isPrivacyRoute" class="standalone-container">
      <router-outlet></router-outlet>
    </div>

    <!-- Authenticated View (main app with topbar + sidebar) -->
    <div *ngIf="isAuthenticated && !isPrivacyRoute" class="authenticated-container" [class.shell--sidebar-collapsed]="sidebarCollapsed">
      
      <!-- Logo and breadcrumb are direct grid cells (real boxes). A display:contents
           wrapper has no renderer, and iPad Safari then never commits the tap. -->
      <div class="topbar-brand">
          <div class="topbar-brand__slot">
            <!-- Hamburger: below ~1024px only (also hidden in SCSS — Tailwind lg:hidden loses to encapsulated display:flex) -->
            <button
              type="button"
              class="mobile-menu-toggle lg:hidden"
              (click)="toggleMobileMenu()"
              aria-label="Otevřít menu"
              aria-haspopup="true"
              [attr.aria-expanded]="mobileMenuOpen"
              aria-controls="mobile-nav-drawer"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
            <a [routerLink]="shellLandingPath" class="logo-section" aria-label="TelcoTrack">
              <img src="assets/images/ProTelco.png" alt="TelcoTrack" class="logo-small" />
            </a>
          </div>
        </div>

        <header class="topbar-trail">
          <nav class="topbar-breadcrumb" aria-label="Drobečková navigace">
            <ol class="topbar-breadcrumb__list">
              <li *ngFor="let crumb of shellCrumbs; let last = last" class="topbar-breadcrumb__item">
                <a *ngIf="crumb.link" class="topbar-breadcrumb__link" [routerLink]="crumb.link">{{ crumb.label }}</a>
                <span *ngIf="!crumb.link" class="topbar-breadcrumb__current" [attr.aria-current]="last ? 'page' : null">{{ crumb.label }}</span>
                <span *ngIf="!last" class="topbar-breadcrumb__sep" aria-hidden="true">›</span>
              </li>
            </ol>
          </nav>

        <div class="nav-right">
          <!-- Notifications -->
          <div #notificationsDropdownContainer class="relative topbar-action">
            <button
              type="button"
              class="btn btn-icon relative topbar-action-btn"
              [ngClass]="notificationsDropdownOpen ? 'btn-primary' : 'btn-secondary'"
              [class.topbar-action-btn--open]="notificationsDropdownOpen"
              (click)="toggleNotificationsDropdown()"
              aria-label="Oznámení"
              aria-haspopup="true"
              [attr.aria-expanded]="notificationsDropdownOpen"
              aria-controls="notifications-panel"
            >
              <svg class="w-5 h-5 topbar-action-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
              </svg>
              <span *ngIf="unreadCount > 0" class="notification-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
            </button>
            
            <div
              *ngIf="notificationsDropdownOpen"
              id="notifications-panel"
              class="notification-dropdown tt-dropdown-enter"
              role="dialog"
              aria-label="Oznámení"
            >
              <div class="notification-dropdown-header">
                <h3 class="notification-dropdown-title">Oznámení</h3>
                <button *ngIf="unreadCount > 0" type="button" class="notification-mark-all-btn" (click)="markAllNotificationsAsRead()">Označit vše jako přečtené</button>
                <app-filter-multi-select
                  class="notification-dropdown-filter"
                  label="Stav"
                  icon="status"
                  [options]="notificationFilterOptions"
                  [selected]="selectedNotificationFilters"
                  emptyLabel="Všechna oznámení"
                  (selectedChange)="onNotificationFilterChange($event)"
                ></app-filter-multi-select>
              </div>
              <div class="notification-list">
                <p *ngIf="notificationActionError" class="notification-action-error">{{ notificationActionError }}</p>
                <ng-container *ngIf="notifications.length > 0; else emptyNotifications">
                  <div
                    *ngFor="let n of notifications"
                    class="notification-item"
                    [class.notification-item-unread]="!n.readAt"
                  >
                    <button
                      type="button"
                      class="notification-item-body"
                      (click)="onNotificationClick(n)"
                    >
                      <span class="notification-item-message">{{ n.message }}</span>
                      <span class="notification-item-time">{{ formatNotificationTime(n.createdAt) }}</span>
                    </button>
                    <div *ngIf="isTransferConfirmNotification(n) && !n.readAt" class="notification-item-actions">
                      <app-button
                        variant="secondary"
                        size="sm"
                        [loading]="transferActionNotificationId === n.id"
                        [disabled]="!!transferActionNotificationId && transferActionNotificationId !== n.id"
                        (onClick)="rejectTransferFromNotification(n, $event)"
                      >{{ transferRejectActionLabel }}</app-button>
                      <app-button
                        variant="primary"
                        size="sm"
                        [loading]="transferActionNotificationId === n.id"
                        [disabled]="!!transferActionNotificationId && transferActionNotificationId !== n.id"
                        (onClick)="confirmTransferFromNotification(n, $event)"
                      >{{ transferConfirmActionLabel(n) }}</app-button>
                    </div>
                  </div>
                </ng-container>
                <ng-template #emptyNotifications>
                  <div class="notification-empty">
                    <svg class="notification-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                    </svg>
                    <p>Žádná oznámení</p>
                  </div>
                </ng-template>
              </div>
            </div>
          </div>

          <!-- Účet: stejné menu jako dřív ve sidebaru (jméno + odhlášení). -->
          <div #settingsDropdownContainer class="relative topbar-action">
            <button
              type="button"
              class="btn btn-icon relative topbar-action-btn"
              [ngClass]="settingsDropdownOpen ? 'btn-primary' : 'btn-secondary'"
              [class.topbar-action-btn--open]="settingsDropdownOpen"
              (click)="toggleSettingsDropdown()"
              [attr.aria-label]="'Menu účtu: ' + currentUserDisplayName"
              [attr.title]="currentUserDisplayName"
              aria-haspopup="true"
              [attr.aria-expanded]="settingsDropdownOpen"
              aria-controls="user-menu-panel"
            >
              <svg class="w-5 h-5 topbar-action-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
            </button>
            <div
              *ngIf="settingsDropdownOpen"
              id="user-menu-panel"
              class="settings-dropdown settings-dropdown--topbar absolute z-50 tt-dropdown-enter"
              role="menu"
              aria-label="Účet"
            >
              <div class="settings-dropdown-user">
                <span class="settings-dropdown-user__name">{{ currentUserDisplayName }}</span>
                <span
                  *ngIf="currentUserEmail && currentUserEmail !== currentUserDisplayName"
                  class="settings-dropdown-user__email"
                >{{ currentUserEmail }}</span>
              </div>
              <button
                type="button"
                class="settings-dropdown-item"
                role="menuitem"
                (click)="onLogout()"
              >
                <svg class="settings-dropdown-item__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                </svg>
                <span class="settings-dropdown-item__label">Odhlásit se</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            class="btn btn-icon btn-secondary topbar-action-btn"
            (click)="onThemeToggle()"
            [attr.aria-label]="currentTheme === 'light' ? 'Přepnout na tmavý režim' : 'Přepnout na světlý režim'"
            [attr.aria-pressed]="currentTheme === 'dark'"
            [attr.title]="themeToggleLabel"
          >
            <svg *ngIf="currentTheme === 'light'" class="w-5 h-5 topbar-action-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path>
            </svg>
            <svg *ngIf="currentTheme === 'dark'" class="w-5 h-5 topbar-action-btn__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>
            </svg>
          </button>
        </div>
        </header>

        <!-- Side Navigation (desktop only, >=1024px).
             On /app-nastaveni only Nastavení is listed. Otherwise categories stay visible.
             The open category lists its sections; a nested entity lists back + its tabs. -->
        <aside class="sidebar sidebar--level hidden lg:flex flex-col shrink-0 bg-background-secondary"
               [class.sidebar--collapsed]="sidebarCollapsed"
               [class.dark]="currentTheme === 'dark'"
               style="border-right: 1px solid rgb(var(--color-border-primary) / 0.15);">
          <nav class="sidebar-nav flex-1 flex flex-col">
            <ul class="sidebar-list flex-1"
                #sidebarNavList
                [class.sidebar-list--indicator-ready]="sidebarIndicatorReady"
                [style.--sidebar-indicator-y.px]="sidebarIndicatorY"
                [style.--sidebar-indicator-h.px]="sidebarIndicatorH">
              <li class="sidebar-section-heading sidebar-section-heading--with-toggle">
                <div class="sidebar-section-heading__row">
                  <button type="button"
                          class="sidebar-toggle"
                          (click)="toggleSidebar()"
                          [attr.aria-label]="sidebarCollapsed ? 'Rozbalit postranní panel' : 'Sbalit postranní panel'"
                          [attr.title]="sidebarCollapsed ? 'Rozbalit' : 'Sbalit'">
                    <svg class="sidebar-toggle-icon" [class.sidebar-toggle-icon--collapsed]="sidebarCollapsed" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                    </svg>
                  </button>
                </div>
              </li>
              <ng-container *ngTemplateOutlet="shellLevelNav; context: { navId: 'desk' }"></ng-container>
            </ul>

            <ng-template #shellLevelNav let-navId="navId">
              <li *ngFor="let mod of shellCategories; trackBy: trackShellModule" class="sidebar-category">
                <!-- Nastavení: Zpět sits above the category heading (not under it as the parent page name). -->
                <a
                  *ngIf="isSettingsModule(mod)"
                  class="nav-link nav-link--hub-back"
                  [attr.href]="settingsReturnLink"
                  aria-label="Zpět"
                  (click)="leaveAppSettings($event)"
                >
                  <svg class="nav-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  <span class="nav-link-text">Zpět</span>
                </a>
                <a
                  *ngIf="isLeafCategory(mod)"
                  [routerLink]="mod.route"
                  class="nav-link nav-link--category"
                  [class.active]="mod.id === shellModule?.id"
                  (click)="closeAccountMenu()"
                >
                  <svg
                    *ngIf="moduleIconPaths(mod).length; else leafCategoryMark"
                    class="nav-link-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      *ngFor="let d of moduleIconPaths(mod)"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      [attr.d]="d"
                    ></path>
                  </svg>
                  <ng-template #leafCategoryMark>
                    <span class="nav-link-mark" aria-hidden="true">{{ mod.label.slice(0, 1) }}</span>
                  </ng-template>
                  <span class="nav-link-text">{{ mod.label }}</span>
                </a>
                <button
                  *ngIf="!isLeafCategory(mod)"
                  type="button"
                  class="nav-link nav-link--category"
                  [class.active]="isShellCategoryCurrent(mod)"
                  [attr.aria-expanded]="isCategoryExpanded(mod.id)"
                  [attr.aria-controls]="navId + '-shell-cat-' + mod.id"
                  (click)="toggleShellCategory(mod.id)"
                >
                  <svg
                    *ngIf="moduleIconPaths(mod).length; else categoryMark"
                    class="nav-link-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      *ngFor="let d of moduleIconPaths(mod)"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      [attr.d]="d"
                    ></path>
                  </svg>
                  <ng-template #categoryMark>
                    <span class="nav-link-mark" aria-hidden="true">{{ mod.label.slice(0, 1) }}</span>
                  </ng-template>
                  <span class="nav-link-text">{{ mod.label }}</span>
                  <svg
                    class="nav-link-chevron"
                    [class.nav-link-chevron--open]="isCategoryExpanded(mod.id)"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
                <div
                  *ngIf="!isLeafCategory(mod)"
                  class="sidebar-disclosure"
                  [class.sidebar-disclosure--open]="showCategoryChildren(mod)"
                >
                <div class="sidebar-disclosure__clip">
                <ul
                  class="sidebar-category__children"
                  [id]="navId + '-shell-cat-' + mod.id"
                >
                  <li *ngFor="let section of categorySections(mod); trackBy: trackShellSection">
                    <a
                      [routerLink]="section.route"
                      class="nav-link nav-link--child"
                      [class.active]="isShellSectionCurrent(mod, section)"
                      (click)="closeAccountMenu()"
                    >
                      <svg
                        *ngIf="sectionIconPaths(section).length; else sectionMark"
                        class="nav-link-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          *ngFor="let d of sectionIconPaths(section)"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          [attr.d]="d"
                        ></path>
                      </svg>
                      <ng-template #sectionMark>
                        <span class="nav-link-mark" aria-hidden="true">{{ section.label.slice(0, 1) }}</span>
                      </ng-template>
                      <span class="nav-link-text">{{ section.label }}</span>
                    </a>
                    <div
                      class="sidebar-disclosure"
                      [class.sidebar-disclosure--open]="sectionPanelOpen(mod, section)"
                    >
                    <div class="sidebar-disclosure__clip">
                    <ul
                      *ngIf="sectionBack(mod, section) || sectionChildRows(mod, section).length"
                      class="sidebar-category__children"
                    >
                      <li *ngIf="sectionBack(mod, section) as back">
                        <a [routerLink]="back.link" class="nav-link nav-link--nested nav-link--hub-back" (click)="closeAccountMenu()">
                          <svg class="nav-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                          </svg>
                          <span class="nav-link-text">{{ back.label }}</span>
                        </a>
                      </li>
                      <ng-container *ngFor="let row of sectionChildRows(mod, section); trackBy: trackTopbarSectionTabRow">
                        <li *ngFor="let tab of row.tabs; trackBy: trackShellTab">
                          <button
                            type="button"
                            class="nav-link nav-link--nested"
                            [class.active]="isShellChildCurrent(mod, section, row, tab.id)"
                            [disabled]="!!tab.disabled"
                            (click)="onShellSectionTab(row, tab.id)"
                          >
                            <ng-container *ngTemplateOutlet="shellSubcatIcon; context: { tab: tab }"></ng-container>
                            <span class="nav-link-text">{{ tab.label }}</span>
                          </button>
                        </li>
                      </ng-container>
                    </ul>
                    </div>
                    </div>
                  </li>
                </ul>
                </div>
                </div>
              </li>
              <ng-template #shellSubcatIcon let-tab="tab">
                <svg class="nav-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    *ngFor="let d of tabIconPaths(tab)"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    [attr.d]="d"
                  ></path>
                </svg>
              </ng-template>
            </ng-template>

            <div class="sidebar-footer" *ngIf="canViewAppSettings">
              <a
                class="nav-link"
                [class.active]="shellSettingsOpen"
                [attr.href]="shellSettingsOpen ? settingsReturnLink : '/app-nastaveni'"
                aria-label="Nastavení"
                title="Nastavení"
                (click)="onSettingsGear($event)"
              >
                <svg class="nav-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <span class="nav-link-text">Nastavení</span>
              </a>
            </div>
          </nav>
        </aside>

        <!-- Mobile Navigation Drawer (below ~1024px): off-canvas, backdrop, focus-trapped.
             Full text labels always visible (no icon-only collapse on mobile). -->
        <div *ngIf="mobileMenuOpen" #mobileNavRoot class="mobile-nav-root">
        <div
          class="mobile-nav-backdrop lg:hidden"
          (click)="closeMobileMenu()"
          aria-hidden="true"
        ></div>
        <aside
          #mobileNavDrawer
          id="mobile-nav-drawer"
          class="mobile-nav-drawer lg:hidden flex flex-col"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          aria-label="Hlavní navigace"
          (keydown)="onMobileMenuKeydown($event)"
        >
          <div class="mobile-nav-drawer-header">
            <img src="assets/images/ProTelco.png" alt="ProTelco" class="logo-small" />
            <button type="button" class="mobile-nav-close" (click)="closeMobileMenu()" aria-label="Zavřít menu">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <ul class="mobile-nav-list flex-1">
            <ng-container *ngTemplateOutlet="shellLevelNav; context: { navId: 'drawer' }"></ng-container>
          </ul>
          <div class="mobile-nav-drawer-footer" *ngIf="canViewAppSettings">
            <a
              class="nav-link"
              [class.active]="shellSettingsOpen"
              [attr.href]="shellSettingsOpen ? settingsReturnLink : '/app-nastaveni'"
              aria-label="Nastavení"
              (click)="onSettingsGear($event)"
            >
              <svg class="nav-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              <span class="nav-link-text">Nastavení</span>
            </a>
          </div>
        </aside>
        </div>

        <!-- Page Content -->
        <!-- min-w-0: override flex min-width:auto so wide content cannot paint over the sidebar -->
        <main class="min-h-0 min-w-0 app-page-scroll">
          <router-outlet></router-outlet>
        </main>
    </div>

  </div>

  <div class="toast-container">
    <app-toast-host></app-toast-host>
  </div>
  <div class="modal-container"></div>

  <!-- Globální modal Stavebního deníku (automaticky se otevírá po commitu v polygonu) -->
  <app-diary-entry-modal
    [isOpen]="diaryModalOpen"
    [entry]="activeDiaryEntry"
    [canConfirm]="canConfirmDiary"
    (closeEvent)="onDiaryModalClose()"
    (saved)="onDiaryEntrySaved($event)"
    (confirmed)="onDiaryEntryConfirmed($event)">
  </app-diary-entry-modal>

</div>
